import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { NavigationEnd, Router } from '@angular/router';
import { AppFeatureExtensions } from '@app/app-feature-extensions.config';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { PlatformService } from '@core/services/platform.service';
import { isTruthy, removeDuplicateSpaces } from '@shared';

import { LayoutConfig, LayoutPrivileges } from '../layout.abstract.config';
import { LayoutService } from '../layout.service';
import * as LayoutActions from '../store/layout.actions';
import { DESKTOP_LIST_SIZE } from '../store/layout.effects';
import {
  getGlobalSearchLoading,
  getGlobalSearchResultsWithCompanies,
} from '../store/layout.selectors';
import { appConfig } from '@app/app.config';
import { featureAccessIsActive } from 'mojo-feature-access';
import { MojoFeatureExtensionsService } from 'mojo-feature-extensions';

const S_KEY = 83;

@Component({
  selector: 'mojo-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit, OnDestroy {
  privileges: LayoutPrivileges;
  showGlobalSearch = false;
  globalSearch = new UntypedFormControl('');
  subs = new Subscription();
  @ViewChild('menuTrigger') menuTrigger;

  searchResults$ = this.store.select(getGlobalSearchResultsWithCompanies);

  globalSearchLoading$ = this.store.select(getGlobalSearchLoading);

  referralReport = AppFeatureExtensions.REFERRAL_REPORT;
  conversionAndRoiReport = AppFeatureExtensions.CONVERSION_AND_ROI_REPORT;
  usageReport = AppFeatureExtensions.USAGE_REPORT;
  projectsReport = AppFeatureExtensions.PROJECTS_REPORT;
  hasNewReportsAccess$ = combineLatest([
    this.store.select(featureAccessIsActive(this.conversionAndRoiReport)),
    this.store.select(featureAccessIsActive(this.usageReport)),
    this.store.select(featureAccessIsActive(this.projectsReport)),
  ]).pipe(map((accesses) => accesses.some(Boolean)));
  talentPoolsPrivilege = appConfig.privileges.talentPools.view;
  talentPoolsFe = AppFeatureExtensions.TALENT_POOLS;
  reports = signal(false);
  fullMenu = signal(false);
  newReportsActive = signal(false);
  isPBIReportsFEOn$: Observable<boolean> = this.mojoFeatureExtensionsService.hasFeatureExtension(
    AppFeatureExtensions.IRP_PBI_REPORT,
  );
  isReportsWithOtherMeanuFEOn$: Observable<boolean> =
    this.mojoFeatureExtensionsService.hasFeatureExtension(
      AppFeatureExtensions.PBI_REPORTS_WITH_OTHER_MENU_ITEMS,
    );

  constructor(
    private store: Store<{}>,
    private platformService: PlatformService,
    private elRef: ElementRef,
    private layoutService: LayoutService,
    private layoutConfig: LayoutConfig,
    private mojoFeatureExtensionsService: MojoFeatureExtensionsService,
    private router: Router,
  ) {
    this.privileges = this.layoutConfig.privileges;
  }

  @HostListener('window:keydown', ['$event']) spaceEvent(event) {
    if (event.altKey && event.keyCode === S_KEY) {
      this.toggleGlobalSearchInput();
      if (this.showGlobalSearch) {
        // Use setTimeout to wait for DOM rendering
        setTimeout(() => {
          this.elRef.nativeElement.querySelector('mojo-input input').focus();
        }, 0);
      } else {
        this.menuTrigger.closeMenu();
      }
    }
  }

  ngOnInit() {
    this.handleTransitionFromMobileToDesktop();
    this.emitValueOnInputChange();
    this.handleDesktopGlobalSearchMenuAndQueryForResults();
    this.subs.add(
      this.router.events
        .pipe(
          filter((event): event is NavigationEnd => event instanceof NavigationEnd),
          map((event) => event.urlAfterRedirects.startsWith('/new-reports')),
        )
        .subscribe((isActive) => {
          this.newReportsActive.set(isActive);
        }),
    );
    this.subs.add(
      combineLatest([this.isPBIReportsFEOn$, this.isReportsWithOtherMeanuFEOn$])
        .pipe(
          map(([isPBIReportsFEOn, isReportsWithOtherMeanuFEOn]) => {
            this.reports.set(isPBIReportsFEOn);
            this.fullMenu.set(isReportsWithOtherMeanuFEOn);
          }),
        )
        .subscribe(),
    );
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  toggleGlobalSearchInput() {
    this.showGlobalSearch = !this.showGlobalSearch;
  }

  closeGlobalSearch() {
    this.store.dispatch(LayoutActions.layoutHeaderClearGlobalSearch());
    this.toggleGlobalSearchInput();
    this.globalSearch.reset();
  }

  showMenu(menuTrigger: MatMenuTrigger, searchedPhrase: string) {
    if (searchedPhrase && searchedPhrase.length > 2) {
      menuTrigger.openMenu();
    }
  }

  private handleTransitionFromMobileToDesktop() {
    this.subs.add(
      this.platformService.isMobileView$.pipe(filter(isTruthy)).subscribe(() => {
        this.globalSearch.setValue('');
        if (this.menuTrigger) {
          this.menuTrigger.closeMenu();
        }
      }),
    );
  }

  private emitValueOnInputChange() {
    this.subs.add(
      this.globalSearch.valueChanges.subscribe((search) => {
        this.layoutService.emitGlobalSearch(removeDuplicateSpaces(search));
      }),
    );
  }

  private handleDesktopGlobalSearchMenuAndQueryForResults() {
    this.subs.add(
      this.layoutService.globalSearchDesktop$.subscribe((search) => {
        this.menuTrigger.openMenu();
        this.store.dispatch(
          LayoutActions.layoutHeaderLoadGlobalSearch({
            search,
            apiParams: { size: DESKTOP_LIST_SIZE },
          }),
        );
      }),
    );
  }

  showContainer = (container: string): boolean => {
    const reports = this.reports();
    const fullMenu = this.fullMenu();

    if (reports && fullMenu) {
      return true;
    }

    if (reports && !fullMenu) {
      return container === 'pbi-report';
    }

    if (!reports) {
      return container !== 'pbi-report';
    }

    return false;
  };
}
